const Joi = require('joi');
const FieldTypeUtils = require('./fieldTypes');
const FormLayoutUtils = require('./layout');

const UseFormValidationUtils = module.exports = {};

UseFormValidationUtils.getJoiErrorMessage = (result = {}) => result.error?.details?.[0]?.message || result.error?.message || null;

UseFormValidationUtils.runJoiValidation = ({
    schema,
    value,
    context
}) => {

    if (!schema || typeof schema.validate !== 'function') {
        return null;
    }

    return UseFormValidationUtils.getJoiErrorMessage(schema.validate(value, {
        abortEarly: true,
        context
    }));
};

UseFormValidationUtils.getFieldLabel = (definition = {}) => definition.validationLabel || definition.label || definition.name || 'Field';

UseFormValidationUtils.buildMaxLengthMessage = (definition = {}) => `${UseFormValidationUtils.getFieldLabel(definition)} may only be ${definition.maxLength} characters long`;

UseFormValidationUtils.buildRequiredMessage = (definition = {}) => `${UseFormValidationUtils.getFieldLabel(definition)} is required`;

UseFormValidationUtils.hasEmptyValue = (value) => {

    if (Array.isArray(value)) {
        return value.length === 0;
    }

    return value === null || typeof value === 'undefined' || value === '';
};

UseFormValidationUtils.getPath = (obj, path) => {

    if (!path) {
        return obj;
    }

    return String(path).split('.').reduce((current, key) => current?.[key], obj);
};

UseFormValidationUtils.getOwnedFieldName = ({
    definition = {},
    fieldName
}) => {

    if (fieldName === 'specify') {
        return definition.specifyField?.name || definition.specifyFieldName;
    }

    return fieldName;
};

UseFormValidationUtils.getContextValue = ({
    config = {},
    context = {},
    definition = {},
    value
}) => {

    if (typeof config.value !== 'undefined') {
        return config.value;
    }

    if (config.fieldName) {
        const fieldName = UseFormValidationUtils.getOwnedFieldName({
            definition,
            fieldName: config.fieldName
        });

        return fieldName ? context.getFieldValue(fieldName) : undefined;
    }

    if (config.valuePath) {
        return UseFormValidationUtils.getPath(context, config.valuePath);
    }

    if (config.definitionPath) {
        return UseFormValidationUtils.getPath(definition, config.definitionPath);
    }

    return value;
};

UseFormValidationUtils.getRolePermission = ({
    props = {},
    permission
}) => {

    if (!permission) {
        return true;
    }

    return !!props.rolePermissions?.[permission];
};

UseFormValidationUtils.isFieldVisible = ({
    context = {},
    definition = {}
} = {}) => FormLayoutUtils.isVisible({
    item: definition,
    context,
    conditionTypes: context.formConfig?.conditionTypes
});

UseFormValidationUtils.shouldIncludeRenderedField = ({
    field,
    fieldName,
    formConfig
} = {}) => {

    const name = field?.name || fieldName;

    if (!name) {
        return false;
    }

    const resolvedField = field || formConfig.getFieldDefinition?.(name) || { name };

    return resolvedField.render !== false && !resolvedField.skipSubmitValidation;
};

UseFormValidationUtils.getRenderedFieldNames = ({
    formConfig
}) => {
    const renderedFieldNames = new Set();

    const collectFieldNames = (fields = []) => {

        fields.forEach((field) => {
            if (!field) {
                return;
            }

            if (Array.isArray(field.fields)) {
                collectFieldNames(field.fields);
            }

            if (UseFormValidationUtils.shouldIncludeRenderedField({
                field,
                formConfig
            })) {
                renderedFieldNames.add(field.name);
            }
        });
    };

    (formConfig.groups || []).forEach((group) => {
        (group.componentProps?.fieldNames || []).forEach((fieldName) => {
            if (UseFormValidationUtils.shouldIncludeRenderedField({
                fieldName,
                formConfig
            })) {
                renderedFieldNames.add(fieldName);
            }
        });

        collectFieldNames(group.componentProps?.fields);
        collectFieldNames(group.fields);
    });

    (formConfig.groupLayout || []).forEach((group) => {
        collectFieldNames(group.fields);
    });

    return renderedFieldNames;
};

UseFormValidationUtils.getSubmitOnlyFieldNames = ({
    formConfig
}) => {
    const submitOnlyFieldNames = new Set();

    (formConfig.groupLayout || []).forEach((groupLayoutItem) => {
        const groupConfig = formConfig.getGroupConfig?.(groupLayoutItem.name);

        (groupConfig?.fields || []).forEach((field) => {
            if (field?.validateOnSubmit && !field.skipSubmitValidation) {
                submitOnlyFieldNames.add(field.name);
            }
        });
    });

    return submitOnlyFieldNames;
};

UseFormValidationUtils.buildValidationFieldNames = ({
    allFields = [],
    formConfig
}) => {
    const renderedFieldNames = UseFormValidationUtils.getRenderedFieldNames({
        formConfig
    });
    const submitOnlyFieldNames = UseFormValidationUtils.getSubmitOnlyFieldNames({
        formConfig
    });

    return allFields.filter((fieldName) => {
        const definition = formConfig.getFieldDefinition?.(fieldName);

        if (definition?.skipSubmitValidation) {
            return false;
        }

        return renderedFieldNames.has(fieldName) || submitOnlyFieldNames.has(fieldName);
    });
};

UseFormValidationUtils.buildDefaultJoiSchema = (definition = {}) => {

    if (!definition.required && !definition.maxLength) {
        return null;
    }

    let schema = Joi.any();

    if (FieldTypeUtils.isTextFieldType(definition.type) || definition.component === 'TextareaField') {
        schema = Joi.string();
    }

    if (!definition.required && schema.allow) {
        schema = schema.allow('', null);
    }
    else if (definition.required && schema.required) {
        schema = schema.required();
    }

    if (definition.maxLength && schema.max) {
        schema = schema.max(definition.maxLength);
    }

    if (schema.messages) {
        schema = schema.messages({
            'any.required': UseFormValidationUtils.buildRequiredMessage(definition),
            'string.empty': UseFormValidationUtils.buildRequiredMessage(definition),
            'string.max': UseFormValidationUtils.buildMaxLengthMessage(definition)
        });
    }

    return schema;
};

UseFormValidationUtils.normalizeValidationConfig = (validateWith) => {

    if (!validateWith) {
        return [];
    }

    return (Array.isArray(validateWith) ? validateWith : [validateWith])
        .filter(Boolean)
        .map((config) => typeof config === 'string' ? { type: config } : config);
};

UseFormValidationUtils.validators = {
    joi({
        config,
        context,
        definition,
        value
    }) {

        return UseFormValidationUtils.runJoiValidation({
            schema: config.schema || definition.joi || definition.schema,
            value,
            context: {
                ...context,
                definition,
                value
            }
        });
    },

    required({
        config,
        context,
        definition,
        value
    }) {

        if (!UseFormValidationUtils.getRolePermission({
            props: context.props,
            permission: config.permission
        })) {
            return null;
        }

        const valueToCheck = UseFormValidationUtils.getContextValue({
            config,
            context,
            definition,
            value
        });

        return UseFormValidationUtils.hasEmptyValue(valueToCheck)
            ? (config.message || UseFormValidationUtils.buildRequiredMessage(definition))
            : null;
    },

    requiredWhenOptions({
        config,
        context,
        definition,
        value
    }) {

        if (!UseFormValidationUtils.getRolePermission({
            props: context.props,
            permission: config.permission
        })) {
            return null;
        }

        const options = config.options || context.props?.[config.optionsSource || definition.optionsSource];

        if (!Array.isArray(options) || !options.length) {
            return null;
        }

        return UseFormValidationUtils.validators.required({
            config,
            context,
            definition,
            value
        });
    },

    maxLength({
        config,
        context,
        definition,
        value
    }) {

        const valueToCheck = UseFormValidationUtils.getContextValue({
            config,
            context,
            definition,
            value
        });
        const maxLength = config.maxLength || definition.maxLength;

        if (!valueToCheck || !maxLength || valueToCheck.length <= maxLength) {
            return null;
        }

        return config.message || UseFormValidationUtils.buildMaxLengthMessage({
            ...definition,
            maxLength,
            label: config.label || definition.label
        });
    },

    namedSelection({
        config,
        context,
        definition
    }) {

        if (!UseFormValidationUtils.getRolePermission({
            props: context.props,
            permission: config.permission
        })) {
            return null;
        }

        const textValue = context.getFieldValue(config.textFieldName || definition.textFieldName);
        const selectedValue = context.getFieldValue(config.selectedFieldName || definition.name);
        const selectedName = UseFormValidationUtils.getPath(selectedValue, config.selectedPath);

        if (textValue && !selectedName) {
            return config.message || 'Please choose an item in the list';
        }

        if (!config.runRefValidation) {
            return null;
        }

        const refName = config.refName || definition.refName || definition.componentKey || definition.name;
        const refValidation = context.inputRefs?.[refName]?.current?.validate;

        return typeof refValidation === 'function' ? refValidation() : null;
    },

    choiceWithSpecify({
        config,
        context,
        definition,
        value
    }) {

        const shouldRequire = typeof config.permission === 'string'
            ? UseFormValidationUtils.getRolePermission({
                props: context.props,
                permission: config.permission
            })
            : config.required !== false && !!definition.required;
        const specifyOptionValue = config.specifyOptionValue || definition.specifyOptionValue || 'specify';
        const isSpecifySelectionPending = value === specifyOptionValue;

        if (shouldRequire && (UseFormValidationUtils.hasEmptyValue(value) || isSpecifySelectionPending)) {
            return config.requiredMessage || UseFormValidationUtils.buildRequiredMessage(definition);
        }

        const specifyField = definition.specifyField || {};
        const specifyFieldName = UseFormValidationUtils.getOwnedFieldName({
            definition,
            fieldName: config.specifyFieldName || definition.specifyFieldName || specifyField.name || 'specify'
        });
        const specifyValue = specifyFieldName ? context.getFieldValue(specifyFieldName) : '';
        const maxLength = config.maxLength || config.specifyMaxLength || specifyField.maxLength;

        if (!specifyValue || !maxLength || specifyValue.length <= maxLength) {
            return null;
        }

        return config.maxMessage || `${config.specifyValidationLabel || specifyField.validationLabel || specifyField.label || UseFormValidationUtils.getFieldLabel(definition)} may only be ${maxLength} characters`;
    },

    arrayLength({
        config,
        value
    }) {

        const count = Array.isArray(value) ? value.length : 0;

        if (typeof config.min === 'number' && count < config.min) {
            return config.minMessage || `Please select at least ${config.min}`;
        }

        if (typeof config.max === 'number' && count > config.max) {
            return config.maxMessage || `Please select no more than ${config.max}`;
        }

        return null;
    }
};

UseFormValidationUtils.buildValidators = (validators = {}) => ({
    ...UseFormValidationUtils.validators,
    ...validators
});

UseFormValidationUtils.runConfiguredValidation = ({
    definition = {},
    value,
    context,
    validators = {}
}) => {
    const validatorsByType = UseFormValidationUtils.buildValidators(validators);

    for (const config of UseFormValidationUtils.normalizeValidationConfig(definition.validateWith || definition.validation)) {
        const validator = typeof config.validate === 'function'
            ? config.validate
            : validatorsByType[config.type];

        if (typeof validator !== 'function') {
            continue;
        }

        const error = validator({
            config,
            context,
            definition,
            value
        });

        if (error) {
            return error;
        }
    }

    return null;
};

UseFormValidationUtils.runFieldValidation = ({
    definition = {},
    value,
    context,
    validators = {}
}) => {

    if (!UseFormValidationUtils.isFieldVisible({
        context,
        definition
    })) {
        return null;
    }

    const configuredError = UseFormValidationUtils.runConfiguredValidation({
        definition,
        value,
        context,
        validators
    });

    if (configuredError) {
        return configuredError;
    }

    const inlineError = typeof definition.validate === 'function'
        ? definition.validate({
            ...context,
            definition,
            value
        })
        : null;

    if (inlineError) {
        return inlineError;
    }

    const explicitJoiError = UseFormValidationUtils.validators.joi({
        config: {},
        context,
        definition,
        value
    });

    if (explicitJoiError) {
        return explicitJoiError;
    }

    if (definition.skipDefaultValidation) {
        return null;
    }

    return UseFormValidationUtils.runJoiValidation({
        schema: UseFormValidationUtils.buildDefaultJoiSchema(definition),
        value,
        context: {
            ...context,
            definition,
            value
        }
    });
};
